/**
 * Coerce mail address fields to display strings.
 * Live API payloads return string | `{name,email}` | arrays | nested `emailAddress`.
 */

function pickString(...candidates: unknown[]): string {
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
  }
  return "";
}

function contactNameEmail(value: unknown): { name: string; email: string } {
  if (value == null) return { name: "", email: "" };
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return { name: "", email: "" };
    const angle = trimmed.match(/^(.*?)\s*<([^>]+@[^>]+)>\s*$/);
    if (angle) {
      return {
        name: angle[1].trim().replace(/^["']|["']$/g, ""),
        email: angle[2].trim(),
      };
    }
    if (trimmed.includes("@") && !/\s/.test(trimmed)) {
      return { name: "", email: trimmed };
    }
    return { name: trimmed, email: "" };
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return { name: String(value), email: "" };
  }
  if (Array.isArray(value)) {
    return { name: "", email: "" };
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (record.emailAddress && typeof record.emailAddress === "object") {
      const nested = contactNameEmail(record.emailAddress);
      if (nested.name || nested.email) return nested;
    }
    const name = pickString(record.name, record.Name, record.displayName, record.DisplayName);
    const email = pickString(
      record.email,
      record.Email,
      record.address,
      record.Address,
      record.value,
      record.Value,
    );
    if (name || email) return { name, email };
    const asString = pickString(record.from, record.From);
    if (asString) return contactNameEmail(asString);
  }
  return { name: "", email: "" };
}

/**
 * Short contact label: name preferred over email for objects.
 * Strings pass through unchanged (may already be `Name <email>`).
 * Use formatMailContactFull when both name and address must stay visible.
 */
export function formatMailContact(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    return value.map(formatMailContact).filter(Boolean).join(", ");
  }
  if (typeof value === "object") {
    const { name, email } = contactNameEmail(value);
    return name || email || "";
  }
  return "";
}

/**
 * Email addresses only (lowercased) from string | `{name,email}` | arrays.
 * `formatMailContact` prefers display names, so reply/reply-all must use this.
 */
export function mailContactEmails(value: unknown): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) return value.flatMap(mailContactEmails);
  if (typeof value === "string") {
    return value
      .split(/[,;]/)
      .map((part) => {
        const trimmed = part.trim();
        const angle = trimmed.match(/<([^<>\s]+@[^<>\s]+)>/);
        if (angle) return angle[1].toLowerCase();
        const bare = trimmed.match(/[^\s<>"']+@[^\s<>"']+/);
        return bare ? bare[0].toLowerCase() : "";
      })
      .filter(Boolean);
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (record.emailAddress && typeof record.emailAddress === "object") {
      const nested = mailContactEmails(record.emailAddress);
      if (nested.length) return nested;
    }
    for (const candidate of [record.email, record.Email, record.address, record.Address]) {
      const found = mailContactEmails(candidate);
      if (found.length) return found;
    }
  }
  return [];
}

/** Gmail-style `Name <email@domain>` when both exist; otherwise name or email alone. */
export function formatMailContactFull(value: unknown): string {
  if (value == null) return "";
  if (Array.isArray(value)) {
    return value.map(formatMailContactFull).filter(Boolean).join(", ");
  }
  const { name, email } = contactNameEmail(value);
  if (name && email) {
    if (name.toLowerCase() === email.toLowerCase()) return email;
    if (name.includes(`<${email}>`) || name.includes(email)) {
      return name.includes("@") ? name : `${name} <${email}>`;
    }
    return `${name} <${email}>`;
  }
  return name || email || (typeof value === "string" ? value.trim() : "");
}

/** Short label for collapsed To/Cc lines (name preferred; strips `Name <email>`). */
export function formatMailContactShort(value: unknown): string {
  if (value == null) return "";
  if (Array.isArray(value)) {
    return value.map(formatMailContactShort).filter(Boolean).join(", ");
  }
  const { name, email } = contactNameEmail(value);
  return name || email || (typeof value === "string" ? value.trim() : formatMailContact(value));
}

export function formatMailAddressList(value: unknown): string {
  if (value == null) return "";
  if (Array.isArray(value)) {
    return value.map(formatMailContact).filter(Boolean).join(", ");
  }
  return formatMailContact(value);
}

export function formatMailAddressListFull(value: unknown): string {
  if (value == null) return "";
  if (Array.isArray(value)) {
    return value.map(formatMailContactFull).filter(Boolean).join(", ");
  }
  return formatMailContactFull(value);
}

export function formatMailAddressListShort(value: unknown): string {
  if (value == null) return "";
  if (Array.isArray(value)) {
    return value.map(formatMailContactShort).filter(Boolean).join(", ");
  }
  return formatMailContactShort(value);
}

/** Email address only, if present. */
export function mailContactEmail(value: unknown): string {
  return contactNameEmail(value).email;
}

/** Combine fromName + fromEmail (or a single from field) into `Name <email>`. */
export function formatMailFromFull(item: {
  fromName?: unknown;
  fromEmail?: unknown;
  from?: unknown;
}): string {
  const name =
    formatMailContact(item.fromName) ||
    (() => {
      const from = contactNameEmail(item.from);
      return from.name;
    })();
  const email =
    contactNameEmail(item.fromEmail).email ||
    contactNameEmail(item.from).email ||
    contactNameEmail(item.fromName).email;
  if (name && email) {
    if (name.toLowerCase() === email.toLowerCase()) return email;
    if (name.includes(`<${email}>`)) return name;
    return `${name} <${email}>`;
  }
  return formatMailContactFull(item.from) || name || email || "";
}

type MailFromSource = {
  fromName?: unknown;
  fromEmail?: unknown;
  from?: unknown;
  participants?: unknown;
  messages?: Array<{
    fromName?: unknown;
    fromEmail?: unknown;
    from?: unknown;
  }>;
};

function fromFields(item: {
  fromName?: unknown;
  fromEmail?: unknown;
  from?: unknown;
}): string {
  return (
    formatMailContact(item.fromName) ||
    formatMailContact(item.fromEmail) ||
    formatMailContact(item.from)
  );
}

/**
 * Prefer fromName → fromEmail → from → participants[0] → first nested message From.
 * Optional `fallback` is connection display name / mailbox label — last resort before "Unknown".
 */
export function displayMailFrom(item: MailFromSource, fallback?: string): string {
  const direct = fromFields(item);
  if (direct) return direct;

  if (Array.isArray(item.participants) && item.participants.length) {
    const participant = formatMailContact(item.participants[0]);
    if (participant) return participant;
  }

  if (Array.isArray(item.messages) && item.messages.length) {
    for (const message of item.messages) {
      if (!message) continue;
      const nested = fromFields(message);
      if (nested) return nested;
    }
  }

  const label = typeof fallback === "string" ? fallback.trim() : "";
  if (label) return label;
  return "Unknown";
}
